import React, { useEffect, useState } from 'react';
import { useAuth } from "../../../context/AuthContext";
import { useDispatch, useSelector } from 'react-redux';
import { getAllFormsData, uploadFinalPdfToS3, createOxyzoEsign, getEsginStatus } from "../../../redux/slice/hrFormSlice";
import {
  Table, Button, Card, Typography, Tag, Space, Tooltip,
  message, Progress, Popconfirm, Badge, Row, Col, Statistic,
  Divider, Modal
} from 'antd';
import {
  FilePdfOutlined, CheckOutlined, CloseOutlined,
  EditOutlined, MergeCellsOutlined, FileExcelOutlined,
  ReloadOutlined, LoadingOutlined, CloudUploadOutlined,
  EyeOutlined, SafetyCertificateOutlined, FileTextOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { trackFeatureAction } from '../../../analytics/googleAnalytics';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;

// Styled components
const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const StatusBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 10px;
    height: 10px;
  }
  .ant-badge-status-text {
    font-size: 13px;
    font-weight: 500;
  }
`;

const PrimaryButton = styled(Button)`
  background: #4f46e5;
  color: white;
  border: none;
  &:hover {
    background: #4338ca !important;
    color: white !important;
  }
`;

const SecondaryButton = styled(Button)`
  background: #e0e7ff;
  color: #4f46e5;
  border: none;
  &:hover {
    background: #c7d2fe !important;
    color: #4f46e5 !important;
  }
`;

const GhostButton = styled(Button)`
  background: transparent;
  color: #4f46e5;
  border: 1px solid #4f46e5;
  &:hover {
    background: #f5f3ff !important;
    color: #4f46e5 !important;
  }
`;

const RefreshButton = styled(Button)`
  position: absolute;
  right: 24px;
  top: 24px;
  background: #f1f5f9;
  border: none;
`;

const StatsCard = styled(Card)`
  border-radius: 8px;
  background: #f8fafc;
  text-align:center;
  .ant-statistic-title {
    color: #64748b;
    font-size: 16px;
  }
  .ant-statistic-content {
    color: #1e293b;
    font-size: 20px;
  }
`;

const DocumentIconWrapper = styled.div`
  display: inline-flex;
  padding: 6px;
  border-radius: 6px;
  background: ${props => props.active ? props.bgColor : '#f1f5f9'};
  cursor: ${props => props.active ? 'pointer' : 'default'};
  transition: all 0.2s;
  &:hover {
    transform: ${props => props.active ? 'translateY(-2px)' : 'none'};
  }
`;

const FormStatus = ({ user_id: propUserId }) => {
  const { authState } = useAuth();
  const dispatch = useDispatch();
  const { 
    emp_esi_data = [], 
    emp_compliance_data = [], 
    emp_esign_data = [] 
  } = useSelector((state) => state.hrForm || {});
  
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState([]);
  const [esignLoadingIds, setEsignLoadingIds] = useState([]);
  const [statusCheckLoadingIds, setStatusCheckLoadingIds] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const jwtBearer = authState.token;

  useEffect(() => {
    trackFeatureAction(
      "MainHrForm",
      "Forms Status",
      "open"
    );
  }, []);

  useEffect(() => {
    fetchData();
  }, [dispatch, jwtBearer]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await dispatch(getAllFormsData({ jwtBearer }));
      message.success('Data refreshed');
    } catch (error) {
      message.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate completion status based on the actual backend data
  const getCompletionStatus = (formData, type) => {
    if (!formData) return 0;
    
    if (type === 'esi') {
      const completedSections = [
        formData.is_section1_completed,
        formData.is_section3_completed,
        formData.is_section4_completed,
        formData.is_section5_completed
      ].filter(Boolean).length;
      
      return Math.round((completedSections / 4) * 100);
    } else if (type === 'compliance') {
      const completedSections = [
        formData.is_section1_completed,
        formData.is_section2_completed,
        formData.is_section3_completed,
        formData.is_section4_completed,
        formData.is_section5_completed
      ].filter(Boolean).length;
      
      return Math.round((completedSections / 5) * 100);
    }
    return 0;
  };

  // Prepare ALL data (unfiltered) for statistics
  const allData = [...new Set([
    ...emp_esi_data.map(e => e.user_id),
    ...emp_compliance_data.map(c => c.user_id),
    ...emp_esign_data.map(e => e.user_id)
  ])].map(userId => {
    const esi = emp_esi_data.find(e => e.user_id === userId) || {};
    const compliance = emp_compliance_data.find(c => c.user_id === userId) || {};
    const esign = emp_esign_data.find(e => e.user_id === userId) || {};
    
    const esiProgress = getCompletionStatus(esi, 'esi');
    const complianceProgress = getCompletionStatus(compliance, 'compliance');
    
    const esiComplete = esiProgress === 100;
    const complianceComplete = complianceProgress === 100;
    
    return {
      key: userId,
      userId,
      esi,
      compliance,
      esign,
      esiComplete,
      complianceComplete,
      esiProgress,
      complianceProgress,
      hasEsiPdf: !!esi?.esi_pdf,
      hasCompliancePdf: !!compliance?.compliance_pdf,
      combinedPdf: esign?.unsigned_pdf,
      has_signed: esign?.has_signed,
      requestId: esign?.requestId,
      signUrl: esign?.sign_url,
      signed_pdf: esign?.signed_pdf,
      audit_trail_pdf: esign?.audit_trail_pdf,
      esign_status: esign?.esign_status,
      employeeName: compliance?.section1?.name || esi?.section1?.name || '',
      employeeEmailId: compliance?.section4?.officialEmail || '',
      employeeAadhar: compliance?.section4?.aadhaarNumber || '',
      eSignInitiated: esign?.['e-sign_initiated'] || false,
      updatedAt: esign?.updated_at || compliance?.updated_at || esi?.updated_at
    };
  });

  // Prepare FILTERED data for the table
  const tableData = allData.filter(record =>
    propUserId ? record.userId === propUserId : true
  );

  // Calculating statistics from ALL data (not filtered)
  const stats = {
    total: allData.length,
    esiCompleted: allData.filter(d => d.esiComplete).length,
    complianceCompleted: allData.filter(d => d.complianceComplete).length,
    withCombined: allData.filter(d => d.combinedPdf).length,
    fullyComplete: allData.filter(d => d.esiComplete && d.complianceComplete).length,
    eSignInitiated: allData.filter(d => d.eSignInitiated).length,
    eSignCompleted: allData.filter(d => d.has_signed === true).length,
    auditTrailAvailable: allData.filter(d => d.audit_trail_pdf).length
  };

  const handleCombinePdf = async (userId) => {
    setProcessingIds(prev => [...prev, userId]);
    try {
      await dispatch(uploadFinalPdfToS3({ user_id: userId, file_type: 'combined' })).unwrap();
      trackFeatureAction(
        "MainHrForm",
        "Forms Status",
        "combine",
        {
          document: "employee_forms",
        }
      );
      message.success(`PDF combining initiated for ${userId}`);
      fetchData();
    } catch (error) {
      message.error(`Failed to process PDF for ${userId}`);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleESign = async (record) => {
    try {
      setEsignLoadingIds(prev => [...prev, record.userId]);
      
      const requestBody = {
        employeeUniqueId: record?.userId,
        employeeName: record?.employeeName,
        employeeEmailId: record?.employeeEmailId,
        // employeeEmailId:"prafool.singh@zielfintech.com",
        // employeeAadhar: "10519dd0a6e24929ba98bebb0820d1a8",
        employeeAadhar: record?.employeeAadhar,
        documentsLink: record?.combinedPdf
      };
      console.log("requestBody",requestBody)
      await dispatch(createOxyzoEsign({ jwtBearer, requestBody })).unwrap();
      trackFeatureAction(
        "MainHrForm",
        "Forms Status",
        "initiate_esign"
      );
      message.success(`eSign initiated for ${record.userId}`);
      fetchData();
    } catch (error) {
      message.error(`Failed to initiate eSign for ${record.userId}`);
      console.error("Error in handleESign:", error);
    } finally {
      setEsignLoadingIds(prev => prev.filter(id => id !== record.userId)); 
    }
  };

  const handleCheckStatus = async (userId, requestId) => {
    console.log("requestId",requestId)
    try {
      setStatusCheckLoadingIds(prev => [...prev, userId]);
      await dispatch(getEsginStatus({ jwtBearer, request_id: requestId })).unwrap();
      trackFeatureAction(
        "MainHrForm",
        "Forms Status",
        "check_esign_status"
      );
      message.success(`Status checked for ${userId}`);
      fetchData();
    } catch (error) {
      message.error(`Failed to check status for ${userId}`);
    } finally {
      setStatusCheckLoadingIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handlePreview = (url, title) => {
    if (!url) {
      message.warning('Document not available');
      return;
    }
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewVisible(true);
  };

  const handleExportExcel = () => {
    const excelData = allData.map(item => ({
      'User ID': item.userId,
      'Employee Name': item.employeeName,
      'ESI Form Completed': item.esiComplete ? 'Yes' : 'No',
      'Compliance Form Completed': item.complianceComplete ? 'Yes' : 'No',
      'Combined PDF Available': item.combinedPdf ? 'Yes' : 'No',
      'eSign Status': item.eSignInitiated ? (item.has_signed ? 'Signed' : 'Pending') : 'Not Initiated',

    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Forms Status");
    XLSX.writeFile(wb, "employee_forms_status.xlsx");
    trackFeatureAction(
      "MainHrForm",
      "Forms Status",
      "download",
      {
        format: "xlsx",
      }
    );
  };

  const getStatusTag = (esign_status) => {
    console.log("esign_status",esign_status)
    if (esign_status === undefined) {
      return <Tag>Not Initiated</Tag>;
    } else if (esign_status === 'SIGNED') {
      return <Tag color="green">Signed</Tag>;
    } else if (esign_status === 'PENDING_SIGN') {
      return <Tag color="yellow">Pending</Tag>;
    } 
  };

  const columns = [
    {
      title: 'Employee ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => <Text strong style={{ color: '#1e293b' }}>{id}</Text>,
      sorter: (a, b) => a.userId.localeCompare(b.userId),
      width: 140,
    },
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 180,
    },
    {
      title: 'ESI Form',
      key: 'esi',
      render: (record) => (
        <Tooltip title={`${record.esiProgress}% completed`}>
          <Progress
            percent={record.esiProgress}
            status={record.esiComplete ? 'success' : 'normal'}
            size="small"
            strokeColor={record.esiComplete ? '#10b981' : '#3b82f6'}
            trailColor="#e2e8f0"
            format={() => (
              <StatusBadge
                status={record.esiComplete ? 'success' : 'error'}
                text={`${record.esiProgress}%`}
              />
            )}
          />
        </Tooltip>
      ),
      sorter: (a, b) => a.esiProgress - b.esiProgress,
      width: 160,
    },
    {
      title: 'Compliance Form',
      key: 'compliance',
      render: (record) => (
        <Tooltip title={`${record.complianceProgress}% completed`}>
          <Progress
            percent={record.complianceProgress}
            status={record.complianceComplete ? 'success' : 'normal'}
            size="small"
            strokeColor={record.complianceComplete ? '#10b981' : '#3b82f6'}
            trailColor="#e2e8f0"
            format={() => (
              <StatusBadge
                status={record.complianceComplete ? 'success' : 'error'}
                text={`${record.complianceProgress}%`}
              />
            )}
          />
        </Tooltip>
      ),
      sorter: (a, b) => a.complianceProgress - b.complianceProgress,
      width: 160,
    },
    {
      title: 'Documents',
      key: 'docs',
      render: (record) => (
        <Space size={8}>
          <Tooltip title={record.hasEsiPdf ? "View ESI PDF" : "No ESI PDF"}>
            <DocumentIconWrapper
              active={record.hasEsiPdf}
              bgColor="#fee2e2"
              onClick={() => record.hasEsiPdf && window.open(record.esi.esi_pdf, '_blank')}
            >
              <FilePdfOutlined style={{
                color: record.hasEsiPdf ? '#ef4444' : '#9ca3af',
                fontSize: 18
              }} />
            </DocumentIconWrapper>
          </Tooltip>
    
          <Tooltip title={record.hasCompliancePdf ? "View Compliance PDF" : "No Compliance PDF"}>
            <DocumentIconWrapper
              active={record.hasCompliancePdf}
              bgColor="#dbeafe"
              onClick={() => record.hasCompliancePdf && window.open(record.compliance.compliance_pdf, '_blank')}
            >
              <FilePdfOutlined style={{
                color: record.hasCompliancePdf ? '#3b82f6' : '#9ca3af',
                fontSize: 18
              }} />
            </DocumentIconWrapper>
          </Tooltip>
    
          <Tooltip title={record.combinedPdf ? "View Combined PDF" : "No Combined PDF"}>
            <DocumentIconWrapper
              active={record.combinedPdf}
              bgColor="#ede9fe"
              onClick={() => record.combinedPdf && window.open(record.combinedPdf, '_blank')}
            >
              <MergeCellsOutlined style={{
                color: record.combinedPdf ? '#8b5cf6' : '#9ca3af',
                fontSize: 18
              }} />
            </DocumentIconWrapper>
          </Tooltip>
    
          {record.signed_pdf && (
            <Tooltip title="View Signed Document">
              <DocumentIconWrapper
                active={true}
                bgColor="#dcfce7"
                onClick={() => window.open(record.signed_pdf, '_blank')}
              >
                <SafetyCertificateOutlined style={{
                  color: '#16a34a',
                  fontSize: 18
                }} />
              </DocumentIconWrapper>
            </Tooltip>
          )}
    
          {record.audit_trail_pdf && (
            <Tooltip title="View Audit Trail">
              <DocumentIconWrapper
                active={true}
                bgColor="#fef3c7"
                onClick={() => window.open(record.audit_trail_pdf, '_blank')}
              >
                <FileTextOutlined style={{
                  color: '#d97706',
                  fontSize: 18
                }} />
              </DocumentIconWrapper>
            </Tooltip>
          )}
        </Space>
      ),
      width: 220,
    },
    {
      title: 'Combined PDF',
      key: 'process',
      render: (record) => (
        <Popconfirm
          title={`Process documents for ${record.userId}?`}
          onConfirm={() => handleCombinePdf(record.userId)}
          okText="Yes"
          cancelText="No"
          disabled={record.eSignInitiated}
        >
          <SecondaryButton
            icon={processingIds.includes(record.userId) ? <LoadingOutlined /> : <CloudUploadOutlined />}
            disabled={!record.hasCompliancePdf || record.eSignInitiated}
          >
            {record.combinedPdf ? 'Re-combine' : 'Combine'}
          </SecondaryButton>
        </Popconfirm>
      ),
      width: 140,
    },
        {
          title: 'eSign Actions',
          key: 'esignActions',
          render: (record) => (
            <Space>
              {!record.eSignInitiated ? (
                <GhostButton
                  icon={<EditOutlined />}
                  onClick={() => handleESign(record)}
                  disabled={!record.combinedPdf}
                  loading={esignLoadingIds.includes(record.userId)}
                >
                  Initiate eSign
                </GhostButton>
              ) : (
                <GhostButton
                  icon={<EyeOutlined />}
                  onClick={() => handleCheckStatus(record.userId, record?.esign?.requestId)}
                  loading={statusCheckLoadingIds.includes(record.userId)}
                  disabled={record.has_signed === true}
                >
                  Check Status
                </GhostButton>
              )}
            </Space>
          ),
          width: 180,
        },
        {
          title: 'eSign Status',
          key: 'esign_status',
          render: (_, record) => (
            <Space>
              {getStatusTag(record.esign_status)}
            </Space>
          ),
          width: 180,
        }
  ];

  return (
    <>
      <StyledCard
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0, color: '#1e293b' }}>
              {propUserId ? `Document Status for ${propUserId}` : 'Employee Document Status'}
            </Title>
            <RefreshButton
              icon={<ReloadOutlined />}
              onClick={() => {
                trackFeatureAction(
                  "MainHrForm",
                  "Forms Status",
                  "refresh"
                );

                fetchData();
              }}
              shape="circle"
            />
          </div>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => {
                trackFeatureAction(
                  "MainHrForm",
                  "Forms Status",
                  "refresh"
                );

                fetchData();
              }}
              loading={loading}
              style={{ marginRight: 8 }}
            >
              Refresh
            </Button>
            <PrimaryButton
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
            >
              Export
            </PrimaryButton>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="Total Employees" value={stats.total} />
            </StatsCard>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="ESI Completed" value={stats.esiCompleted} />
            </StatsCard>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="Compliance Completed" value={stats.complianceCompleted} />
            </StatsCard>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="Both Completed" value={stats.fullyComplete} />
            </StatsCard>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="eSign Initiated" value={stats.eSignInitiated} />
            </StatsCard>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <StatsCard>
              <Statistic title="eSign Completed" value={stats.eSignCompleted} />
            </StatsCard>
          </Col>
       
        </Row>

        <Divider style={{ margin: '16px 0', borderColor: '#e2e8f0' }} />

        <Table
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={{
            pageSize: 10,
            position: ['bottomCenter']
          }}
          size="middle"
          scroll={{ x: true }}
          rowClassName={(record) => {
            if (record.has_signed === true) return 'ant-table-row-success';
            if (record.eSignInitiated) return 'ant-table-row-warning';
            return '';
          }}
          style={{ borderRadius: 8 }}
        />
      </StyledCard>

      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
        destroyOnClose
      >
        <iframe 
          src={previewUrl} 
          style={{ width: '100%', height: '70vh', border: 'none' }}
          title="Document Preview"
        />
      </Modal>
    </>
  );
};

export default FormStatus;